import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
  SelectListResponse,
  TenantsClient,
  UserResponse,
  UsersClient,
} from '../../../generated-client/generated-api-client';
import {Router} from '@angular/router';
import {ErrorProcessorService} from '../../../services/error-processor.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {ValidatorPatterns} from '../../../custom-validators/validatorPatterns';
import {
  isFormModifiedDataService,
  SimpleNavigationService
} from "../../../services/data-services/navigation-data-services";

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.sass'],
})
export class UserFormComponent implements OnInit {
  @Input() response!: Observable<UserResponse>;
  @Output() onSubmitCallback: EventEmitter<any> = new EventEmitter();
  userForm!: FormGroup;
  roleSelectList!: SelectListResponse;
  tenantSelectList!: SelectListResponse;
  isRoleSelectListLoading = new BehaviorSubject<boolean>(true);
  isTenantSelectListLoading = new BehaviorSubject<boolean>(true);
  isFormLoading = new BehaviorSubject<boolean>(false);
  formResponse?: UserResponse;
  isPageReady: boolean = false;

  constructor(
    private router: Router,
    private usersClient: UsersClient,
    private tenantClient: TenantsClient,
    private errorProcessorService: ErrorProcessorService,
    private _isFormModifiedDataService: isFormModifiedDataService,
    private _navigationService: SimpleNavigationService
  ) {}

  ngOnInit(): void {
    this.isTenantSelectListLoading.subscribe(next => this.setFormWhenReady());
    this.isRoleSelectListLoading.subscribe(next => this.setFormWhenReady());
    this.isFormLoading.subscribe(next => this.setFormWhenReady());

    this.subscribeToFormResponse();
    this.subscribeToRoleListResponse();
    this.subscribeToTenantListResponse();

    if (!this.response) {
      this.isFormLoading.next(false);
    }
  }

  private subscribeToFormResponse() {
    this.response?.subscribe(
      response => {
        this.formResponse = response;
        this.isFormLoading.next(false);
      },
      error => this.errorProcessorService.processError(error, false, true)
    );
  }

  private subscribeToRoleListResponse() {
    this.usersClient.roles().subscribe(
      rolesResponse => {
        this.roleSelectList = rolesResponse;
        this.isRoleSelectListLoading.next(false);
      },
      error => this.errorProcessorService.processError(error, false, true)
    );
  }

  private subscribeToTenantListResponse() {
    this.tenantClient.tenants().subscribe(
      tenantResponse => {
        this.tenantSelectList = tenantResponse;
        this.isTenantSelectListLoading.next(false);
      },
      error => this.errorProcessorService.processError(error, false, true)
    );
  }

  private setFormWhenReady() {
    if (!this.isTenantSelectListLoading.value && !this.isRoleSelectListLoading.value && !this.isFormLoading.value) {
      this.setForm(this.formResponse);
    }
  }

  get email() {
    return this.userForm.get('email')!;
  }

  get surname() {
    return this.userForm.get('surname')!;
  }

  get forename() {
    return this.userForm.get('forename')!;
  }

  get forename2() {
    return this.userForm.get('forename2')!;
  }

  get roles() {
    return this.userForm.get('roles')!;
  }

  get tenant() {
    return this.userForm.get('tenantIds')!;
  }

  get idNumber() {
    return this.userForm.get('idNumber')!;
  }

  get title() {
    return this.userForm.get('title')!;
  }

  get twoFactorEnabled() {
    return this.userForm.get('twoFactorEnabled')!;
  }

  onSubmit(): void {
    this.onSubmitCallback.emit([this.userForm]);
  }

  onGoBack(path: string){
    this._navigationService.navigateTo(path);
  }

  setForm(response?: UserResponse) {
    this.userForm = new FormGroup({
      id: new FormControl(response?.id),
      title: new FormControl(response?.title, Validators.maxLength(50)),
      email: new FormControl(response?.email, [
        Validators.pattern(ValidatorPatterns.email),
        Validators.required,
        Validators.maxLength(50),
      ]),
      surname: new FormControl(response?.surname, [Validators.required, Validators.maxLength(50)]),
      forename: new FormControl(response?.forename, [Validators.required, Validators.maxLength(50)]),
      forename2: new FormControl(response?.forename2, [Validators.maxLength(50)]),
      roles: new FormControl(response?.roles, [Validators.required]),
      tenantIds: new FormControl(response?.tenantIds, [Validators.required]),
      idNumber: new FormControl(response?.idNumber, Validators.maxLength(100)),
      twoFactorEnabled: new FormControl(response?.twoFactorEnabled ?? true),
    });
    this.errorProcessorService.formGroup = this.userForm;
    this.isPageReady = true;
    this.userForm.valueChanges.subscribe(() => {
      this._isFormModifiedDataService.isFormModified = true;
    });
  }
}
